import React, { ReactNode, useEffect, useMemo, useRef } from 'react';

import { graphql, useFragment } from 'react-relay';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import { FeatureExposedAnalytics } from '../analytics/feature-exposed-analytics';
import { useAnalytics } from '../analytics/use-analytics';
import {
	SiteInformationContext,
	useEditionAwareness,
	useSiteInformation,
} from '../services/edition-awareness-context';
import { isEligibleForEduDiscount } from '../services/is-eligible-for-edu-discount';
import { isEligibleForStandardTrialsUserAndStorageLimitBreakers } from '../services/is-eligible-for-standard-trials-user-and-storage-limit-breakers';
import type { ExperimentCohort, ExperimentCohortControlVariation } from '../types';

import type { actionsAndContent_AtlassianEditionAwareness$key } from './__generated__/actionsAndContent_AtlassianEditionAwareness.graphql';
import { EducationalDiscountDialog } from './dialogs/educational-discount-modal';
import { FreeToStandardDialog } from './dialogs/free-to-standard';
import { FreeToStandardRefresh } from './dialogs/free-to-standard-refresh';
import { PaymentDetailsPopupDialog } from './dialogs/payment-details-popup';
import { PlanSelectionPageDialog } from './dialogs/plan-selection-page-modal';
import { PremiumAddPayment } from './dialogs/premium-add-payment';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPayment } from './dialogs/standard-add-payment';
import { StandardTrialLimitBreakersDialog } from './dialogs/standard-trial-limit-breakers-modal';
import { EditionAwarenessLink } from './edition-awareness-button';

const AddPaymentLink = ({ trialButton }: { trialButton: ReactNode }) => {
	const context = useEditionAwareness();
	const { addPaymentDetailsExperienceUrl } = context;

	if (!addPaymentDetailsExperienceUrl) {
		// Keep it fallback until we have proper error handling
		return trialButton;
	}

	return (
		<EditionAwarenessLink
			href={addPaymentDetailsExperienceUrl}
			target="_blank"
			touchpointIntent="addPaymentDetails"
			linkId="predunning"
		/>
	);
};

/**
 * Sends an event when there is no action available for the customer to render. This is an error state
 * which means that audience and timing has passed a customer as eligible when they shouldn't have.
 *
 * Next iteration on this would be to return a safe default action for all customers
 */
function NoAction() {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'track',
			actionSubject: 'actionsAndContent',
			action: 'missed',
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent]);

	return null;
}

function FreeEdition({ freeButton }: { freeButton: ReactNode }) {
	const context = useEditionAwareness();
	const { product, siteType, isInTeamWorkCollection } = context;

	if (
		['jira', 'confluence'].includes(product) &&
		isInTeamWorkCollection &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageDialog type="twc" />;
	}

	if (
		isEligibleForEduDiscount(context) &&
		FeatureGates.getExperimentValue<ExperimentCohortControlVariation>(
			'offer_edu_org_discount',
			'cohort',
			'not-enrolled',
		) === 'variation'
	) {
		return (
			<FeatureExposedAnalytics flagKey="offer_edu_org_discount" cohort="variation">
				<EducationalDiscountDialog />
			</FeatureExposedAnalytics>
		);
	}

	if (
		product === 'jira' &&
		// We're excluding the edu experiment cohort from seeing the refresh experiment
		siteType !== 'EDU' &&
		siteType !== 'ORG' &&
		fg('new_ea_jira_free_modal_refresh')
	) {
		const cohort = FeatureGates.getExperimentValue<string>(
			'jira_free_ea_modal_refresh',
			'cohort',
			'not-enrolled',
			{
				// We only want to fire the exposure when they view the modal.
				// The control experience is still inside of the product.
				fireExperimentExposure: false,
			},
		);

		if (cohort === 'variation') {
			return <FreeToStandardRefresh />;
		}
	}

	if (product === 'confluence' && fg('growth_migrate_confluence_free_to_standard_fg')) {
		return <FreeToStandardDialog />;
	}

	if (product === 'jira-product-discovery' && fg('new_ea_jira_product_discovery_free')) {
		return <FreeToStandardDialog />;
	}

	if (product === 'jira' && fg('new_ea_jira_free')) {
		return <FreeToStandardDialog />;
	}

	if (product === 'jira-service-management' && fg('new_ea_jira_service_management_free')) {
		return <FreeToStandardDialog />;
	}

	// Until we migrate the rest of the products to the new button content we'll use the old one from product
	return freeButton;
}

function StandardEdition({ trialButton }: { trialButton: ReactNode }) {
	const context = useEditionAwareness();
	const {
		product,
		isInTrial,
		isInPredunning,
		isInvoiceable,
		canAddPaymentDetails,
		isInTeamWorkCollection,
		userRoles,
	} = context;
	const { licensedUsers, storageUsageInGB } = useSiteInformation();

	if (
		['jira', 'confluence'].includes(product) &&
		isInTrial &&
		isInTeamWorkCollection &&
		fg('teamwork_collection_ea_modal')
	) {
		return <PlanSelectionPageDialog type="twc" />;
	}

	if ((isInTrial || isInPredunning) && isInvoiceable === false && canAddPaymentDetails) {
		if (
			// Only eligible for Confluence and Jira
			isEligibleForStandardTrialsUserAndStorageLimitBreakers({
				context,
				licensedUsers,
				storageUsageInGB,
			})
		) {
			const abExperimentKey =
				product === 'jira'
					? 'jira_edition_awareness_standard_trials'
					: 'connie_edition_awareness_standard_trials';

			const abCohort = FeatureGates.getExperimentValue<ExperimentCohort>(
				abExperimentKey,
				'cohort',
				'not-enrolled',
			);

			if (abCohort === 'treatment') {
				return (
					<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort}>
						<StandardTrialLimitBreakersDialog />
					</FeatureExposedAnalytics>
				);
			}

			if (product === 'confluence' && fg('new_ea_confluence_standard')) {
				return (
					<>
						<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
						<StandardAddPayment />
					</>
				);
			}

			// Site admins get routed to JSTO
			if (
				product === 'jira' &&
				isInTrial &&
				!userRoles?.includes('SITE_ADMIN') &&
				fg('new_ea_jira_payment_details_popup')
			) {
				return (
					<>
						<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
						<PaymentDetailsPopupDialog />
					</>
				);
			}

			return (
				<>
					<FeatureExposedAnalytics flagKey={abExperimentKey} cohort={abCohort} />
					{trialButton}
				</>
			);
		}

		if (
			isInTrial &&
			(product === 'jira-service-management' || product === 'jira-product-discovery') &&
			fg('new_ea_jsw_jpd_standard_in_trial')
		) {
			return <StandardAddPayment />;
		}

		// Site admins get routed to JSTO
		if (
			product === 'jira' &&
			isInTrial &&
			!userRoles?.includes('SITE_ADMIN') &&
			fg('new_ea_jira_payment_details_popup')
		) {
			return <PaymentDetailsPopupDialog />;
		}

		if (product === 'confluence' && fg('new_ea_confluence_standard')) {
			return <StandardAddPayment />;
		}

		if (
			isInPredunning &&
			['jira', 'jira-service-management', 'jira-product-discovery'].includes(product) &&
			fg('new_ea_jsw_jsm_jpd_pre_dunning')
		) {
			return <AddPaymentLink trialButton={trialButton} />;
		}

		// trialButton is returned for all other products + Confluence and Jira sites not eligible
		return trialButton;
	}

	return <NoAction />;
}

function PremiumEdition({ trialButton }: { trialButton: ReactNode }) {
	const context = useEditionAwareness();
	const { product, isInTrial, isInPredunning, isInReverseTrial } = context;

	if (
		product === 'jira' &&
		isInTrial &&
		isInReverseTrial &&
		fg('new_ea_jira_reverse_trial_dropdown')
	) {
		return <ReverseTrialPopupDialog />;
	}

	if (
		product === 'jira' &&
		isInTrial &&
		!isInReverseTrial &&
		fg('new_ea_jira_payment_details_popup')
	) {
		return <PaymentDetailsPopupDialog />;
	}

	if (
		isInPredunning &&
		['jira', 'jira-service-management'].includes(product) &&
		fg('new_ea_jsw_jsm_jpd_pre_dunning')
	) {
		return <AddPaymentLink trialButton={trialButton} />;
	}

	if (product === 'confluence' && fg('new_ea_confluence_premium')) {
		return <PremiumPopup />;
	}

	if (isInTrial && product === 'jira-service-management' && fg('new_ea_jsm_premium')) {
		return <PremiumAddPayment />;
	}

	// Until we migrate the rest of the products to the new button content we'll use the old one from product
	return trialButton;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
function ActionsAndContentImpl({
	freeButton,
	trialButton,
}: {
	freeButton: ReactNode;
	trialButton: ReactNode;
}) {
	const { edition } = useEditionAwareness();

	if (edition === 'free') {
		return <FreeEdition freeButton={freeButton} />;
	}

	if (edition === 'standard') {
		return <StandardEdition trialButton={trialButton} />;
	}

	if (edition === 'premium') {
		return <PremiumEdition trialButton={trialButton} />;
	}

	return <NoAction />;
}

/**
 * Responsible for:
 * - Getting data to determine what to show
 * - Handling loading state
 */
export function ActionsAndContent({
	entitlement: entitlementFragment,
	freeButton,
	trialButton,
}: {
	entitlement: actionsAndContent_AtlassianEditionAwareness$key;
	freeButton?: ReactNode;
	trialButton?: ReactNode;
}) {
	/**
	 * When adding data to this query, you should:
	 *
	 * Ensure that it is behind a config that controls the `@include` to ensure that it does
	 *   not query for the data if it is not required.
	 *
	 * That when this data is fetched it will be used the majority of the time (e.g. if this data is only)
	 *   used for 5% of customers and has a high cost then we should likely load it sequentially.
	 */
	const data = useFragment(
		graphql`
			fragment actionsAndContent_AtlassianEditionAwareness on Query {
				tenantContexts(cloudIds: [$cloudId]) @skip(if: $shouldSkip) {
					entitlementInfo(hamsProductKey: $productKey) {
						entitlement(where: $entitlementFilter) {
							latestUsageForChargeElement(chargeElement: $chargeElement)
								@include(if: $shouldCheckForChargeQuantities)
						}
					}
				}

				jira @include(if: $shouldCheckForStorageUsageJira) {
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: $applicationKey)
				}

				confluence_storage(cloudId: $cloudId) @include(if: $shouldCheckForStorageUsageConfluence) {
					bytesUsed
				}
			}
		`,
		entitlementFragment,
	);

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

	// We need to use the `latestUsageForChargeElement` to get the licensed user count for both free and trialing customers
	const licensedUsersRaw = entitlement?.latestUsageForChargeElement;
	// Assumes that there are always at least one assigned user to cover scenarios
	const licensedUsers = Math.max(licensedUsersRaw || 0, 1);

	const bytesUsed = data?.jira?.attachmentStorageUsed || data?.confluence_storage?.bytesUsed;
	const storageUsageInGB = bytesUsed && bytesUsed / 1_000_000_000;

	const analyticsContext = useMemo(
		() => ({ licensedUsers, storageUsageInGB }),
		[licensedUsers, storageUsageInGB],
	);

	return (
		<AnalyticsContext data={analyticsContext}>
			<SiteInformationContext.Provider value={analyticsContext}>
				<ActionsAndContentImpl trialButton={trialButton} freeButton={freeButton} />
			</SiteInformationContext.Provider>
		</AnalyticsContext>
	);
}
